import { ActionListItem, ActionListBusyIndicator } from "client/Components";
import { useFollowAction } from "client/Features/Hub/KnowledgeItems/Details/Actions/useFollowAction";
import { KnowledgeItemStatisticsModel, getStatistics } from "client/Api/Hub/KnowledgeItems/knowledgeItemViewer";
import {
	ReactElement,
	useCallback,
	useEffect,
	useState
} from "react";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import { localize } from "client_legacy/ndaw/culture";
import { KnowledgeItemCommentNotificationDataViewModel } from "client/Api/Notifications/notifications";

type LoadingResult =
	{
		wasSuccessful: false;
	}
	|
	{
		wasSuccessful: true;
		isFollowing: boolean;
	};

export type KnowledgeItemCommentActionsProps = {
	data: KnowledgeItemCommentNotificationDataViewModel;
};

export const KnowledgeItemCommentActions = (props: KnowledgeItemCommentActionsProps): ReactElement | null => {
	const {
		data
	} = props;

	const { knowledgeItemId } = data;

	const [loadingResult, setLoadingResult] = useState<LoadingResult | null>(null);

	useEffect(
		(): (() => void) => {
			const load = async (knowledgeItemId: number, signal: AbortSignal): Promise<void> => {
				try {
					const { isCurrentUserFollowingComments } = await getStatistics(
						knowledgeItemId,
						signal
					);

					setLoadingResult({
						wasSuccessful: true,
						isFollowing: isCurrentUserFollowingComments
					});
				} catch (e) {
					if (isCancelledError(e)) {
						return;
					}

					console.error(e);

					setLoadingResult({
						wasSuccessful: false
					});
				}
			};

			const cancellation = new AbortController();
			load(knowledgeItemId, cancellation.signal);
			return (): void => cancellation.abort();
		},
		[knowledgeItemId]
	);

	const isFollowingChangeHandler = useCallback(
		(isFollowing: boolean): void => {
			setLoadingResult({
				wasSuccessful: true,
				isFollowing
			});
		},
		[]
	);

	if (loadingResult === null) {
		return (
			<ActionListBusyIndicator />
		);
	}

	if (!loadingResult.wasSuccessful) {
		return null;
	}

	return (
		<FollowActionListItem
			knowledgeItemId={knowledgeItemId}
			isFollowing={loadingResult.isFollowing}
			onIsFollowingChanged={isFollowingChangeHandler}
		/>
	);
};
KnowledgeItemCommentActions.displayName = "KnowledgeItemCommentActions";

type FollowActionListItemProps = {
	knowledgeItemId: number;
	isFollowing: boolean;
	onIsFollowingChanged: (isFollowing: boolean) => void;
};

const FollowActionListItem = (props: FollowActionListItemProps): ReactElement => {
	const {
		knowledgeItemId,
		isFollowing,
		onIsFollowingChanged
	} = props;

	const statisticsChangeHandler = useCallback(
		(statistics: KnowledgeItemStatisticsModel): void => {
			onIsFollowingChanged(statistics.isCurrentUserFollowingComments);
		},
		[onIsFollowingChanged]
	);

	const [
		icon,
		key,
		clickHandler
	] = useFollowAction(
		knowledgeItemId,
		isFollowing,
		statisticsChangeHandler
	);

	return (
		<ActionListItem
			icon={icon}
			text={localize(key)}
			onClick={clickHandler}
			trackingId={isFollowing ? "unfollow" : "follow"}
		/>
	);
};
FollowActionListItem.displayName = "FollowActionListItem";
